import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { fmtMoney, fmtErr } from "../../api";
import Layout from "../../components/Layout";
import { Plus, Trash, Star } from "@phosphor-icons/react";
import { toast } from "sonner";
import { PropertyPinMap } from "../../components/PropertyPinMap";
import { LogWorkRequest } from "../../components/LogWorkRequest";

const FREQS = ["weekly", "bi-weekly", "monthly"];
const SIZES = [["small", "Small"], ["medium", "Medium"], ["large", "Large"], ["estate", "Estate"]];
const EMPTY_SVC = { name: "", frequency: "weekly", rate: "", billing_model: "per_visit", seasonal_total: "", seasonal_limit: "", seasonal_billing: "installments", size_based: false, schedulable: true, season_start: "", season_end: "", extra_visit_rate: "" };
const isSnowSvc = (name) => (name || "").toLowerCase().includes("snow");
const SNOW_MODEL_LABELS = { per_visit: "Per visit", monthly: "Monthly", seasonal: "Seasonal pkg" };

export default function PropertyDetail() {
  const { id } = useParams();
  const [p, setP] = useState(null);
  const [services, setServices] = useState([]);
  const [subs, setSubs] = useState([]);
  const [owners, setOwners] = useState([]);
  const [settings, setSettings] = useState(null);
  const [showTransfer, setShowTransfer] = useState(false);
  const [showDeleteProp, setShowDeleteProp] = useState(false);
  const [pinPos, setPinPos] = useState(null);
  const [radiusM, setRadiusM] = useState("");
  const navigate = useNavigate();
  const [showLogWork, setShowLogWork] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [eform, setEform] = useState({ name: "", address: "", access_notes: "", is_str: false });
  const [season, setSeason] = useState({ season_start: "05-01", season_end: "10-31" });

  const load = () => {
    api.get(`/properties/${id}`).then((r) => {
      setP(r.data);
      setServices(r.data.agreement?.services?.map((s) => ({
        ...EMPTY_SVC, ...s, rate: String(s.rate), _k: crypto.randomUUID(),
        seasonal_total: s.seasonal_total ?? "", seasonal_limit: s.seasonal_limit ?? "",
        season_start: s.season_start || "", season_end: s.season_end || "",
        extra_visit_rate: s.extra_visit_rate ?? "",
      })) || []);
      if (r.data.agreement) setSeason({ season_start: r.data.agreement.season_start || "05-01", season_end: r.data.agreement.season_end || "10-31" });
    }).catch((e) => toast.error(fmtErr(e)));
    api.get("/roster-options").then((r) => setSubs(r.data)).catch(() => {});
    api.get("/owners").then((r) => setOwners(r.data)).catch(() => {});
    api.get("/settings").then((r) => setSettings(r.data)).catch(() => {});
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps -- reload when the route id changes; load is stable
  useEffect(() => { load(); }, [id]);

  const transferCandidates = useMemo(() => owners.filter((o) => o.id !== p?.owner_id && o.active !== false), [owners, p?.owner_id]);

  if (!p) return <Layout title="Property"><p className="text-muted-foreground">Loading…</p></Layout>;

  const setSvc = (i, patch) => setServices(services.map((x, j) => (j === i ? { ...x, ...patch } : x)));
  const snowModels = settings?.snow_billing_offered?.length ? settings.snow_billing_offered : ["per_visit", "monthly", "seasonal"];
  const effectiveModel = (s) => {
    if (!isSnowSvc(s.name)) return s.billing_model || "per_visit";
    if (snowModels.includes(s.billing_model)) return s.billing_model;
    return snowModels.includes(settings?.snow_billing_default) ? settings.snow_billing_default : snowModels[0];
  };

  const saveAgreement = async () => {
    try {
      await api.post(`/properties/${id}/agreement`, {
        season_start: season.season_start, season_end: season.season_end,
        services: services.filter((s) => s.name).map((s) => ({
          name: s.name, frequency: s.frequency, rate: Number(s.rate || 0),
          billing_model: effectiveModel(s),
          seasonal_total: s.seasonal_total ? Number(s.seasonal_total) : null,
          seasonal_limit: s.seasonal_limit ? Number(s.seasonal_limit) : null,
          seasonal_billing: s.seasonal_billing || "installments",
          size_based: !!s.size_based,
          schedulable: s.schedulable !== false,
          season_start: s.season_start || null,
          season_end: s.season_end || null,
          extra_visit_rate: s.extra_visit_rate ? Number(s.extra_visit_rate) : null,
        })),
      });
      toast.success("Agreement saved — upcoming visits updated to match");
      load();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const agrAction = async (action) => {
    await api.patch(`/agreements/${p.agreement.id}`, { action });
    toast.success(`Agreement ${action}d`);
    load();
  };

  const sendRenewal = async () => {
    try {
      await api.post(`/agreements/${p.agreement.id}/send-renewal`);
      toast.success("Renewal request sent — the owner gets an email and a confirm button");
      load();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const setPreferred = async (sid) => {
    await api.post(`/properties/${id}/preferred-contractor`, { subcontractor_id: sid });
    toast.success("Preferred contractor updated");
    load();
  };

  const setDriveway = async (size) => {
    try {
      await api.patch(`/properties/${id}`, { driveway_size: size });
      toast.success(size ? `Driveway size set to ${size}` : "Driveway size cleared");
      load();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const transfer = async (oid) => {
    try {
      const r = await api.post(`/properties/${id}/transfer`, { new_owner_id: oid });
      toast.success(r.data.note || "Property transferred");
      setShowTransfer(false);
      load();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const deleteProperty = async () => {
    try {
      const r = await api.delete(`/properties/${id}`);
      toast.success(`Property deleted — ${r.data.invoices_removed} invoice(s) and ${r.data.visits_removed} job(s) removed from your records`);
      navigate("/properties");
    } catch (e) { toast.error(fmtErr(e)); }
  };
  const savePin = async () => {
    const body = {};
    if (pinPos) { body.lat = pinPos[0]; body.lng = pinPos[1]; }
    if (radiusM) body.radius_m = Number(radiusM);
    if (body.lat == null && body.radius_m == null) return;
    try {
      await api.post(`/properties/${id}/pin`, body);
      toast.success(pinPos ? "GPS pin saved — check-ins verify against this spot" : "Check-in radius updated");
      setPinPos(null); setRadiusM(""); load();
    } catch (e) { toast.error(fmtErr(e)); }
  };
  const confirmPin = async () => {
    try { await api.post(`/properties/${id}/pin`, { confirm: true }); toast.success("Pin confirmed by hand — flags cleared"); load(); }
    catch (e) { toast.error(fmtErr(e)); }
  };
  const toggleActive = async () => {
    try {
      await api.patch(`/properties/${id}`, { active: p.active === false });
      toast.success(p.active === false ? "Property reactivated — visits can generate again" : "Property deactivated — no new visits will be generated");
      load();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const saveProp = async () => {
    try {
      await api.patch(`/properties/${id}`, { name: eform.name, address: eform.address, access_notes: eform.access_notes, is_str: eform.is_str });
      toast.success("Property updated — an address change re-pins its map location");
      setShowEdit(false);
      load();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  return (
    <Layout title={p.name}>
      <div className="space-y-8">
        <div className="card-flat p-6" data-testid="property-info">
          <div className="flex items-start justify-between gap-2">
            <p className="text-sm text-muted-foreground">{p.address} · {p.owner_name} {p.is_str && "· STR"}</p>
            {p.active === false && <span data-testid="property-inactive-badge" className="shrink-0 rounded-full bg-destructive/10 px-3 py-1 text-xs font-bold text-destructive">Inactive</span>}
          </div>
          {p.access_notes && <p className="mt-2 rounded-md bg-muted p-3 text-sm"><span className="font-bold">Access:</span> {p.access_notes}</p>}
          <button data-testid="property-edit-btn" onClick={() => { setShowEdit(!showEdit); setEform({ name: p.name, address: p.address, access_notes: p.access_notes || "", is_str: !!p.is_str }); }}
            className="mt-3 h-10 rounded-md border border-border px-4 text-xs font-bold">Edit details</button>
          {showEdit && (
            <div className="mt-3 space-y-2" data-testid="property-edit-form">
              <input data-testid="prop-edit-name" className="field-input w-full" placeholder="Property name" value={eform.name} onChange={(e) => setEform({ ...eform, name: e.target.value })} />
              <input data-testid="prop-edit-address" className="field-input w-full" placeholder="Address" value={eform.address} onChange={(e) => setEform({ ...eform, address: e.target.value })} />
              <textarea data-testid="prop-edit-notes" className="field-input h-20 w-full py-3" placeholder="Access notes (gate code, parking…)" value={eform.access_notes} onChange={(e) => setEform({ ...eform, access_notes: e.target.value })} />
              <label className="flex items-center gap-2 text-sm font-semibold">
                <input data-testid="prop-edit-str" type="checkbox" checked={eform.is_str} onChange={(e) => setEform({ ...eform, is_str: e.target.checked })} className="h-5 w-5" /> Short-term rental
              </label>
              <button data-testid="prop-edit-save" onClick={saveProp} className="h-12 w-full rounded-md bg-primary text-sm font-bold text-primary-foreground">Save property</button>
            </div>
          )}
          {settings?.offers_snow !== false && (
          <div className="mt-4">
            <p className="label-caps mb-2">Driveway size (snow pricing)</p>
            <div className="flex flex-wrap gap-2">
              <button data-testid="driveway-none" onClick={() => setDriveway(null)}
                className={`h-12 rounded-md border px-4 text-sm font-bold ${!p.driveway_size ? "border-primary bg-primary text-primary-foreground" : "border-border"}`}>Not set</button>
              {SIZES.map(([k, label]) => (
                <button key={k} data-testid={`driveway-${k}`} onClick={() => setDriveway(k)}
                  className={`h-12 rounded-md border px-4 text-sm font-bold ${p.driveway_size === k ? "border-primary bg-primary text-primary-foreground" : "border-border"}`}>
                  {label}{settings?.snow_size_rates?.[k] != null ? ` · ${fmtMoney(settings.snow_size_rates[k])}` : ""}
                </button>
              ))}
            </div>
          </div>
          )}
          <div className="mt-4">
            <p className="label-caps mb-2">Owner's preferred contractor</p>
            <div className="flex flex-wrap gap-2">
              <button data-testid="pref-none" onClick={() => setPreferred(null)}
                className={`h-12 rounded-md border px-4 text-sm font-bold ${!p.preferred_contractor_id ? "border-primary bg-primary text-primary-foreground" : "border-border"}`}>None</button>
              {subs.map((s) => (
                <button key={s.id} data-testid={`pref-${s.id}`} onClick={() => setPreferred(s.id)}
                  className={`h-12 rounded-md border px-4 text-sm font-bold ${p.preferred_contractor_id === s.id ? "border-primary bg-primary text-primary-foreground" : "border-border"}`}>
                  {s.name} {p.preferred_contractor_id === s.id && <Star size={12} weight="fill" className="inline" />}
                </button>
              ))}
            </div>
          </div>
          <div className="mt-4">
            <button data-testid="log-work-btn" onClick={() => setShowLogWork(!showLogWork)}
              className="h-12 w-full rounded-md bg-secondary font-bold text-secondary-foreground">Log work request</button>
            {showLogWork && (
              <div className="mt-3 rounded-md border border-border p-3" data-testid="log-work-panel">
                <LogWorkRequest propertyId={id} onDone={() => setShowLogWork(false)} testid="log-work" />
              </div>
            )}
          </div>
          <div className="mt-4 flex gap-2">
            <button data-testid="transfer-property-btn" onClick={() => setShowTransfer(!showTransfer)}
              className="h-12 flex-1 rounded-md border border-border text-sm font-bold">Transfer to another owner</button>
            <button data-testid="toggle-property-active" onClick={toggleActive}
              className={`h-12 flex-1 rounded-md border text-sm font-bold ${p.active === false ? "border-primary text-primary" : "border-destructive text-destructive"}`}>
              {p.active === false ? "Reactivate property" : "Deactivate property"}
            </button>
          </div>
          <CoOwnersCard propertyId={id} coOwners={p.co_owners || []} onChanged={load} />
          <section className="mt-4 space-y-2" data-testid="pin-section">
            <p className="label-caps">Service location pin</p>
            {(p.lat == null || p.lng == null) && (
              <div className="rounded-md border border-amber-400 bg-amber-50 p-3 dark:bg-amber-950/30" data-testid="no-coords-warning">
                <p className="text-sm font-bold text-amber-700 dark:text-amber-400">No GPS pin — check-ins here can't verify location</p>
                <p className="text-xs text-muted-foreground">
                  {p.geocode_attempts ? `Geocoding couldn't resolve this address (${p.geocode_attempts} attempt${p.geocode_attempts === 1 ? "" : "s"}) — rural addresses often don't. ` : ""}
                  Drop the pin on the actual service spot below and it takes effect immediately.
                </p>
              </div>
            )}
            <PropertyPinMap lat={p.lat} lng={p.lng} onChange={setPinPos} />
            <p className="text-xs text-muted-foreground">Tap the map to drop the pin, or drag it to fine-tune. {p.lat != null && pinPos === null ? `Current pin: ${p.lat.toFixed(5)}, ${p.lng.toFixed(5)}${p.geocode_source === "manual" ? " (set by hand)" : " (geocoded — unverified)"}` : ""}</p>
            <div className="rounded-md border border-border p-3">
              <label className="block text-sm font-semibold">Check-in radius (metres)
                <input data-testid="radius-input" type="number" min="30" max="1000" step="10" className="field-input mt-1 w-full"
                  placeholder={p.geocode_source === "manual" ? "100" : "300"} value={radiusM} onChange={(e) => setRadiusM(e.target.value)} />
              </label>
              <p className="mt-1 text-xs text-muted-foreground" data-testid="effective-radius">
                Effective radius now: <strong>{p.checkin_radius_m ? `${p.checkin_radius_m}m (custom)` : p.geocode_source === "manual" ? "100m (verified pin)" : "300m (pin not verified yet)"}</strong>
                {" "}— tighten it for dense streets, loosen it for big rural lots or condo grounds.
              </p>
            </div>
            {p.lat != null && p.geocode_source !== "manual" && !p.pin_confirmed && (
              <button data-testid="confirm-pin-btn" onClick={confirmPin}
                className="h-12 w-full rounded-md border border-primary text-sm font-bold text-primary">
                I've eyeballed it — this pin is correct
              </button>
            )}
            <button data-testid="save-pin-btn" onClick={savePin} disabled={!pinPos && !radiusM}
              className="h-12 w-full rounded-md bg-primary text-sm font-bold text-primary-foreground disabled:opacity-40">
              {pinPos ? (p.lat == null ? "Save pin" : "Update pin") : "Save radius"}
            </button>
          </section>
          <div className="mt-3" data-testid="property-danger-zone">
            {!showDeleteProp ? (
              <button data-testid="delete-property-btn" onClick={() => setShowDeleteProp(true)} className="text-xs font-bold text-destructive underline underline-offset-2">Delete this property and all its data</button>
            ) : (
              <div className="space-y-2 rounded-md border border-destructive/40 p-3" data-testid="delete-property-form">
                <p className="text-xs text-muted-foreground">Removes {p.name} with all its visits, invoices, agreements and history — they disappear from revenue and HST totals. Blocked while unpaid invoices are outstanding. This can't be undone.</p>
                <div className="grid grid-cols-2 gap-2">
                  <button data-testid="delete-property-back" onClick={() => setShowDeleteProp(false)} className="h-12 rounded-md border border-border text-sm font-bold">Keep it</button>
                  <button data-testid="delete-property-confirm" onClick={deleteProperty} className="h-12 rounded-md bg-destructive text-sm font-bold text-destructive-foreground">Delete everything</button>
                </div>
              </div>
            )}
          </div>
          {showTransfer && (
            <div className="mt-3 space-y-2 rounded-md border border-border p-3" data-testid="transfer-panel">
              <p className="text-xs text-muted-foreground">
                Billing-safe: existing invoices stay billed to the current owner; new visits and invoices bill the new owner. Pause or end the agreement first if recurring billing shouldn't continue.
              </p>
              <div className="flex flex-wrap gap-2">
                {transferCandidates.map((o) => (
                  <button key={o.id} data-testid={`transfer-to-${o.id}`} onClick={() => transfer(o.id)}
                    className="h-12 rounded-md border border-border px-4 text-sm font-bold transition-colors hover:border-primary">{o.name}</button>
                ))}
                {transferCandidates.length === 0 && (
                  <p className="text-xs text-muted-foreground">No other active owners — invite one from the Owners page first.</p>
                )}
              </div>
            </div>
          )}
        </div>

        <section className="card-flat space-y-3 p-6" data-testid="claim-report">
          <p className="label-caps">Claim evidence report</p>
          <p className="text-sm text-muted-foreground">PDF of every completed visit in the range — completion/check-in/out times, accuracy, route verification, map snapshot, photos and salt. Built for slip-and-fall claims.</p>
          <div className="flex items-center gap-2">
            <input type="date" id="claim-from" data-testid="claim-from" className="field-input flex-1"
              defaultValue={new Date(Date.now() - 90 * 864e5).toISOString().slice(0, 10)} />
            <span className="text-sm text-muted-foreground">to</span>
            <input type="date" id="claim-to" data-testid="claim-to" className="field-input flex-1"
              defaultValue={new Date().toISOString().slice(0, 10)} />
          </div>
          <button data-testid="claim-report-btn"
            onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/properties/${p.id}/claim-report?from_date=${document.getElementById("claim-from").value}&to_date=${document.getElementById("claim-to").value}`, "_blank")}
            className="h-14 w-full rounded-md bg-primary text-sm font-bold text-primary-foreground">Download claim report (PDF)</button>
        </section>

        <section data-testid="agreement-editor">
          <div className="mb-3 flex items-center justify-between">
            <p className="label-caps">Maintenance agreement</p>
            {p.agreement && (
              <div className="flex gap-2">
                <button data-testid="send-renewal" onClick={sendRenewal} className="h-10 rounded-md border border-primary px-3 text-xs font-bold text-primary">Send renewal</button>
                {p.agreement.status === "active" && <button data-testid="pause-agreement" onClick={() => agrAction("pause")} className="h-10 rounded-md border border-border px-3 text-xs font-bold">Pause</button>}
                {p.agreement.status === "paused" && <button data-testid="resume-agreement" onClick={() => agrAction("resume")} className="h-10 rounded-md border border-border px-3 text-xs font-bold">Resume</button>}
                <button data-testid="end-agreement" onClick={() => agrAction("end")} className="h-10 rounded-md border border-destructive px-3 text-xs font-bold text-destructive">End</button>
              </div>
            )}
          </div>
          {p.agreement && <p className="mb-3 text-sm font-semibold capitalize" data-testid="agreement-status">Status: {p.agreement.status}{p.agreement.renewal_status ? ` · renewal: ${p.agreement.renewal_status}` : ""}</p>}
          <div className="mb-3 flex gap-2">
            <input data-testid="agr-season-start" className="field-input flex-1" placeholder="Season start MM-DD (e.g. 05-01)" value={season.season_start}
              onChange={(e) => setSeason({ ...season, season_start: e.target.value })} />
            <input data-testid="agr-season-end" className="field-input flex-1" placeholder="Season end MM-DD (e.g. 10-31)" value={season.season_end}
              onChange={(e) => setSeason({ ...season, season_end: e.target.value })} />
          </div>
          <p className="mb-3 text-xs text-muted-foreground">Default season window for services below — wrap-around works (snow: 11-01 → 04-30). A service can override with its own window.</p>
          <div className="space-y-3">
            {services.map((s, i) => (
              <div key={s._k} className="card-flat space-y-2 p-4" data-testid={`service-row-${i}`}>
                <div className="flex gap-2">
                  <input data-testid={`svc-name-${i}`} className="field-input flex-1" placeholder="Service (e.g. Snow removal — driveway)" value={s.name}
                    onChange={(e) => { const v = e.target.value; setSvc(i, { name: v, ...(isSnowSvc(v) ? { billing_model: effectiveModel({ name: v, billing_model: s.billing_model }) } : {}) }); }} />
                  <button data-testid={`svc-del-${i}`} onClick={() => setServices(services.filter((_, j) => j !== i))} className="h-12 w-12 rounded-md border border-border text-destructive"><Trash size={18} className="mx-auto" /></button>
                </div>
                {!isSnowSvc(s.name) && (
                  <>
                    <p className="text-xs font-bold text-muted-foreground">How often (schedule)</p>
                    <div className="flex gap-2">
                      {FREQS.map((f) => (
                        <button key={f} data-testid={`svc-freq-${i}-${f}`} onClick={() => setSvc(i, { frequency: f })}
                          className={`h-10 flex-1 rounded-md border text-xs font-bold ${s.frequency === f ? "border-primary bg-primary text-primary-foreground" : "border-border"}`}>{f}</button>
                      ))}
                    </div>
                  </>
                )}
                {isSnowSvc(s.name) ? (
                  snowModels.length > 1 ? (
                    <>
                      <p className="text-xs font-bold text-muted-foreground">When to invoice (snow)</p>
                      <div className="flex gap-2">
                        {snowModels.map((m) => (
                          <button key={m} data-testid={`svc-billing-${i}-${m}`} onClick={() => setSvc(i, { billing_model: m })}
                            className={`h-10 flex-1 rounded-md border text-xs font-bold ${effectiveModel(s) === m ? "border-primary bg-primary text-primary-foreground" : "border-border"}`}>{SNOW_MODEL_LABELS[m]}</button>
                        ))}
                      </div>
                    </>
                  ) : (
                    <p className="rounded-md bg-muted p-2 text-xs font-semibold" data-testid={`svc-snow-billing-fixed-${i}`}>
                      Snow billing: {SNOW_MODEL_LABELS[snowModels[0]]} — the only model offered (Settings → Snow billing)
                    </p>
                  )
                ) : (
                  <>
                    <p className="text-xs font-bold text-muted-foreground">When to invoice</p>
                    <div className="flex gap-2">
                      {[["per_visit", "Per visit"], ["monthly", "Monthly"], ["seasonal", "Seasonal pkg"]].map(([m, label]) => (
                        <button key={m} data-testid={`svc-billing-${i}-${m}`} onClick={() => setSvc(i, { billing_model: m })}
                          className={`h-10 flex-1 rounded-md border text-xs font-bold ${(s.billing_model || "per_visit") === m ? "border-primary bg-primary text-primary-foreground" : "border-border"}`}>{label}</button>
                      ))}
                    </div>
                  </>
                )}
                {effectiveModel(s) === "per_visit" && (
                  <p className="rounded-md bg-muted p-2 text-xs font-semibold" data-testid={`svc-pervisit-hint-${i}`}>
                    Per visit: a draft invoice is created automatically each time a job is completed.
                  </p>
                )}
                {effectiveModel(s) === "monthly" && (
                  <p className="rounded-md bg-muted p-2 text-xs font-semibold" data-testid={`svc-monthly-hint-${i}`}>
                    {isSnowSvc(s.name)
                      ? "Monthly snow fee: one flat fee per month inside the season window, billed with the monthly run — regardless of snowfall."
                      : "Monthly bundle: no invoice when a job completes — completed visits collect into one invoice per property. Generate it anytime from Invoices → Run monthly billing (it also runs automatically at month end)."}
                  </p>
                )}
                <div className="flex gap-2">
                  {settings?.offers_snow !== false && (
                  <button data-testid={`svc-sizebased-${i}`} onClick={() => setSvc(i, { size_based: !s.size_based })}
                    className={`h-10 flex-1 rounded-md border text-xs font-bold ${s.size_based ? "border-primary bg-primary text-primary-foreground" : "border-border"}`}>Size-based rate</button>
                  )}
                  {effectiveModel(s) === "seasonal" && !isSnowSvc(s.name) && (
                    <button data-testid={`svc-schedulable-${i}`} onClick={() => setSvc(i, { schedulable: s.schedulable === false })}
                      className={`h-10 flex-1 rounded-md border text-xs font-bold ${s.schedulable !== false ? "border-primary bg-primary text-primary-foreground" : "border-border"}`}>
                      {s.schedulable !== false ? "Schedules visits" : "Bills only"}
                    </button>
                  )}
                </div>
                {s.size_based && (
                  <p className="rounded-md bg-muted p-2 text-xs font-semibold" data-testid={`svc-sizebased-hint-${i}`}>
                    Rate auto-fills from driveway size: {p.driveway_size ? `${p.driveway_size} → ${fmtMoney(settings?.snow_size_rates?.[p.driveway_size] ?? 0)}/visit` : "set a driveway size above (defaults to medium)"}
                    {s.billing_model === "seasonal" && s.seasonal_limit && !s.seasonal_total ? ` · season total auto = rate × ${s.seasonal_limit} visits` : ""}
                  </p>
                )}
                {effectiveModel(s) === "seasonal" && (
                  <div className="space-y-2">
                    <div className="flex gap-2">
                      {[["installments", "Monthly installments"], ["one_time", "One seasonal invoice"]].map(([m, label]) => (
                        <button key={m} data-testid={`svc-sbilling-${i}-${m}`} onClick={() => setSvc(i, { seasonal_billing: m })}
                          className={`h-10 flex-1 rounded-md border text-xs font-bold ${(s.seasonal_billing || "installments") === m ? "border-primary bg-primary text-primary-foreground" : "border-border"}`}>{label}</button>
                      ))}
                    </div>
                    <input data-testid={`svc-limit-${i}`} type="number" className="field-input w-full" placeholder="Max visits included this season (optional — extras flag 'over limit')" value={s.seasonal_limit}
                      onChange={(e) => setSvc(i, { seasonal_limit: e.target.value })} />
                    {isSnowSvc(s.name) && (
                      <input data-testid={`svc-extrarate-${i}`} type="number" className="field-input w-full" placeholder="Extra visit rate $ beyond the limit (optional)" value={s.extra_visit_rate}
                        onChange={(e) => setSvc(i, { extra_visit_rate: e.target.value })} />
                    )}
                    {s.seasonal_limit !== "" && s.seasonal_limit != null && s.visits_used != null && (
                      <p className="text-xs font-semibold text-muted-foreground" data-testid={`svc-usage-${i}`}>{s.visits_used} of {s.seasonal_limit} visits used this season</p>
                    )}
                    <div className="flex gap-2">
                      <input data-testid={`svc-sstart-${i}`} className="field-input flex-1" placeholder={`Season start MM-DD (default ${season.season_start})`} value={s.season_start}
                        onChange={(e) => setSvc(i, { season_start: e.target.value })} />
                      <input data-testid={`svc-send-${i}`} className="field-input flex-1" placeholder={`Season end MM-DD (default ${season.season_end})`} value={s.season_end}
                        onChange={(e) => setSvc(i, { season_end: e.target.value })} />
                    </div>
                  </div>
                )}
                {effectiveModel(s) === "seasonal" ? (
                  <input data-testid={`svc-seasonal-${i}`} type="number" className="field-input w-full" placeholder={s.size_based ? "Season total $ (optional — auto = size rate × visit limit)" : "Season total $ (e.g. 1500)"} value={s.seasonal_total}
                    onChange={(e) => setSvc(i, { seasonal_total: e.target.value })} />
                ) : (
                  !s.size_based && (
                    <input data-testid={`svc-rate-${i}`} type="number" className="field-input w-full" placeholder="Rate per visit $" value={s.rate}
                      onChange={(e) => setSvc(i, { rate: e.target.value })} />
                  )
                )}
              </div>
            ))}
          </div>
          <button data-testid="add-service-btn" onClick={() => setServices([...services, { ...EMPTY_SVC, _k: crypto.randomUUID() }])}
            className="mt-3 flex h-12 w-full items-center justify-center gap-2 rounded-md border border-dashed border-border text-sm font-bold text-muted-foreground">
            <Plus size={16} weight="bold" /> Add service
          </button>
          <button data-testid="save-agreement-btn" onClick={saveAgreement} className="btn-primary-big mt-4 !h-14 !text-base">
            {p.agreement ? "Save agreement" : "Create agreement"}
          </button>
          <p className="mt-2 text-xs text-muted-foreground">Recurring rates are pre-approved — invoices are raised automatically. Per-visit bills on completion; monthly bills with the monthly run; seasonal packages bill as monthly installments or one seasonal invoice, across the service's own season window. Snow is reactive: start a snow event from the Today board when it snows and every covered property gets a visit to dispatch.</p>
        </section>
      </div>
    </Layout>
  );
}


function CoOwnersCard({ propertyId, coOwners, onChanged }) {
  const [f, setF] = useState({ name: "", email: "", phone: "" });
  const [busy, setBusy] = useState(false);
  const add = async () => {
    if (!f.name.trim() || !f.email.trim()) { toast.error("Name and email are required"); return; }
    setBusy(true);
    try {
      const r = await api.post(`/properties/${propertyId}/co-owners`, f);
      toast.success(r.data.email_sent ? `${r.data.co_owner.name} added — invite email sent` : `${r.data.co_owner.name} linked as co-owner`);
      setF({ name: "", email: "", phone: "" });
      onChanged();
    } catch (e) { toast.error(fmtErr(e)); }
    setBusy(false);
  };
  const remove = async (uid, name) => {
    try {
      await api.delete(`/properties/${propertyId}/co-owners/${uid}`);
      toast.success(`${name} removed from this property`);
      onChanged();
    } catch (e) { toast.error(fmtErr(e)); }
  };
  return (
    <div className="mt-4 space-y-2 border-t border-border pt-4" data-testid="co-owners-section">
      <p className="label-caps">Household &amp; co-owners</p>
      <p className="text-xs text-muted-foreground">
        Co-owners get full portal access to this property — visits, photos, invoices, approvals, payments — and every notification. Add a spouse or partner here instead of creating a duplicate property.
      </p>
      {coOwners.map((c) => (
        <div key={c.id} className="flex items-center justify-between gap-2 rounded-md border border-border p-3" data-testid={`co-owner-${c.id}`}>
          <div>
            <p className="text-sm font-bold">{c.name}</p>
            <p className="text-xs text-muted-foreground">{c.email}</p>
          </div>
          <button data-testid={`co-owner-remove-${c.id}`} onClick={() => remove(c.id, c.name)}
            className="h-10 shrink-0 rounded-md border border-destructive px-3 text-xs font-bold text-destructive">Remove</button>
        </div>
      ))}
      <input data-testid="co-owner-name" className="field-input w-full" placeholder="Co-owner name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} />
      <input data-testid="co-owner-email" className="field-input w-full" placeholder="Email" type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} />
      <input data-testid="co-owner-phone" className="field-input w-full" placeholder="Phone (optional)" value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} />
      <button data-testid="co-owner-add" onClick={add} disabled={busy}
        className="h-12 w-full rounded-md bg-primary text-sm font-bold text-primary-foreground disabled:opacity-40">
        Add co-owner (existing account or invite by email)
      </button>
    </div>
  );
}
